import React, { JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { VolumeReportsContext } from "@app/reports/volume-reports/import/VolumeReportsContext";
import { reportFilterOptions, VolumeReportsState, WizardStepProps, reportFormatOptions } from "@app/reports/volume-reports/import/interfaces";
import { RouteComponentProps } from "react-router-dom";
import { VolumeReportsStateAccordion } from "@app/reports/volume-reports/import/VolumeReportsStateAccordion";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import SharedUsers from "@app/reports/SharedUsers";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { MembershipType } from "@app/exports/enumerations";
import { toast } from "react-toastify";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import Features from "@app/feature-flags/features";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESCard } from "@app/shared/qes-standards/Card";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";

const VolumeReportsConfig = (props: WizardStepProps & RouteComponentProps): JSX.Element => {
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const user = useContext(UserContext);
  const isCCIIOPPFReportRoleEnabled = useContext(UserContext).roles.includes(Roles.CCIIO_PPF_REPORT_USER);
  const isDistinctServicingProviderReportEnabled = useContext(UserContext).roles.includes(Roles.DISTINCT_SERVICING_USER) && enabledFeatureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT);
  const wizardContext = useContext(VolumeReportsContext);
  const isProvidersRoute = useRef(props.location.pathname.includes("providers")).current;
  const selectedProjects = useRef(wizardContext.projects.filter((p) => p.isChecked));
  const selectedServiceAreas = useRef(
    wizardContext.serviceAreas.some((state) => state.checkboxState !== CheckboxStateEnum.Unchecked) ?
      wizardContext.serviceAreas.reduce<VolumeReportsState[]>((previous, current) => {
        if (current.checkboxState !== CheckboxStateEnum.Unchecked) {
          current.childServiceAreas = current.childServiceAreas.filter((county) => county.isChecked);
          previous.push(current);
        }
        return previous;
      }, []) :
      wizardContext.serviceAreas.map((state) => ({
        ...state,
        checkboxState: CheckboxStateEnum.Checked,
        childServiceAreas: state.childServiceAreas.map((county) => ({
          ...county,
          isChecked: true,
          isVisible: true,
        })),
        isVisible: true,
      })));
  const selectedSpecialties = useRef(
    wizardContext.specialties.some((specialty) => specialty.isChecked) ?
      wizardContext.specialties.filter((specialty) => specialty.isChecked) :
      wizardContext.specialties.map((specialty) => ({ ...specialty, isChecked: true, isVisible: true }))
  );
  const totalCounties = useRef(selectedServiceAreas.current.reduce((previous, current) => previous + current.childServiceAreas.length, 0));

  useEffect(() => {
    props.pageIsValid(wizardContext.reportName !== "");
  }, [wizardContext.reportName]);

  useEffect(() => {
    selectedServiceAreas.current.forEach((item) => {
      item.isVisible = true;
      item.childServiceAreas.forEach((child) => {
        child.isVisible = true;
      })
    })
  }, []);

  useEffect(() => {
    wizardContext.setRemoveNonHSDColumns(!isRemoveNonHsdColumnsDisabled);
  }, [wizardContext.reportFilter, wizardContext.splitReport]);

  const noServiceAreaSelected = useMemo(() => {
    return wizardContext.serviceAreas.every((state) => state.checkboxState === CheckboxStateEnum.Unchecked);
  }, [wizardContext.serviceAreas]);

  const noSpecialtySelected = useMemo(() => {
    return wizardContext.specialties.every((specialty) => !specialty.isChecked);
  }, [wizardContext.specialties])

  const reportFormatRadioButtons = useMemo(() => {
    const options = reportFormatOptions.map((opt) => {
      const key = opt.toLowerCase().replaceAll(" ", "-");
      return (
        <QESRadioButton
          key={opt}
          label={opt}
          testId={`volume-reports__report-format--${key}`}
          onChange={() => { wizardContext.setReportFormat(opt) }}
          checked={wizardContext.reportFormat === opt}
          name="report-format"
          required={true}
        />
      );
    });
    return options;
  }, [reportFormatOptions, wizardContext.reportFormat]);

  const isMembershipComparison = enabledFeatureFlags.has(Features.VOLUME_PROVIDER_REPORT_MEMBERSHIP_FILTER)
    && isProvidersRoute
    && wizardContext.membershipTypeFilter === MembershipType.Both;

  // WI 194524: RPPO tiles can only export MA HSD for Network providers, and a selection can't mix RPPO and non-RPPO projects
  const checkedProjects = wizardContext.projects.filter((p) => p.isChecked);
  const rppoProjectCount = checkedProjects.filter((p) => p.rppoNonContractedProvidersIncludedInHsd).length;
  const isRppoRuleEnabled = enabledFeatureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION);
  // HSD is a fixed-schema output, so MA HSD can't be combined with a custom column preset (mirrors the project-level report)
  const isCustomColumnPresetSelected = enabledFeatureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) && wizardContext.selectedColumnPreset?.id > 0;
  const rppoHsdFormatDisabledTooltip = !isRppoRuleEnabled || rppoProjectCount === 0
    ? undefined
    : rppoProjectCount < checkedProjects.length
      ? "MA HSD cannot combine RPPO and non-RPPO projects."
      : wizardContext.providerType === ProviderNetworkTypeEnum.Prospect || wizardContext.providerType === ProviderNetworkTypeEnum.Market
        ? "This option is available only for Network provider sets."
        : undefined;
  const hsdFormatDisabledTooltip = isCustomColumnPresetSelected
    ? "MA HSD Format isn’t available with a custom column configuration."
    : rppoHsdFormatDisabledTooltip;

  useEffect(() => {
    if (hsdFormatDisabledTooltip && wizardContext.reportFilter === "MA HSD Format") {
      wizardContext.setReportFilter("Default Format");
    }
  }, [hsdFormatDisabledTooltip, wizardContext.reportFilter]);

  const filterRadioButtons = useMemo(() => {
    let filterOptions = reportFilterOptions.slice();
    if (!isCCIIOPPFReportRoleEnabled || isMembershipComparison) {
      filterOptions = filterOptions.filter(o => o !== 'CCIIO PPF Report');
    }

    if (!isDistinctServicingProviderReportEnabled || isMembershipComparison) {
      filterOptions = filterOptions.filter(o => o !== 'Distinct Servicing Providers');
    }

    // Remove the MMP HSD Format option for the providers volume report (or if the feature flag is off)
    if (isProvidersRoute || !enabledFeatureFlags.has(Features.SHOW_MMP_HSD_FORMAT)) {
      filterOptions = filterOptions.filter(o => o !== 'MMP HSD Format');
    }

    const options = filterOptions.map((opt) => {
      const key = opt.toLowerCase().replaceAll(" ", "-");
      const disabledTooltip = opt === "MA HSD Format" ? hsdFormatDisabledTooltip : undefined;
      const radioButton = (
        <QESRadioButton
          key={opt}
          label={opt}
          testId={`volume-reports__report-filter--${key}`}
          disabled={!!disabledTooltip}
          onChange={() => {
            // MMP HSD could be acceptable but we remove it from the options above
            if (opt !== "MA HSD Format" && wizardContext.appendPatternOfCareProviders) {
              wizardContext.setAppendPatternOfCareProviders(false);
              toast("\"Append Pattern of Care Providers\" was unchecked. Select Network Providers in the Provider Filter and MA HSD Format to Append Pattern of Care Providers.", {
                containerId: "z-index-1030",
                type: "info",
              });
            }
            wizardContext.setReportFilter(opt);
          }}
          checked={wizardContext.reportFilter === opt}
          name="report-filter"
          required={true}
        />
      );
      return disabledTooltip
        ? <TooltipWrapper key={opt} containerClassName="d-block" text={disabledTooltip}>{radioButton}</TooltipWrapper>
        : radioButton;
    });
    return options;
  }, [reportFilterOptions, wizardContext.reportFilter, wizardContext.appendPatternOfCareProviders, isMembershipComparison, hsdFormatDisabledTooltip]);

  const getExportType = () => {
    switch (wizardContext.reportType) {
      case "providers":
        return wizardContext.reportFilter != "Default Format" ? "HsdServicingProviderBulk" : "ServicingProviderBulk";
      case "zip-providers":
        return "ZipAnalysisServicingProviderBulk";
      case "stateaggregate":
      case "countyaggregate":
      case "specialtyaggregate":
        return "AdequacyBulkAggregateReport";
      case "specialty":
        return "AdequacySummaryBulk";
      case "zip-summary":
        return "ZipSummaryBulk";
      case "specialty-summary-zip-code":
        return "SpecialtySummaryByZipBulk";
      case "zip-class-aggregate":
        return "ZipClassAggregateResultsReportBulk";
      case "pci":
        return "PciCrossProject";
      case "cciio":
        return "CciioProvider";
      case "cciioDataValidationsSummary":
        return "CCIIOProviderValidationSummary"
      case "cciioDataValidations":
        return "CCIIOValidationDataIntegrity"
    }
  }

  const [alertWhenReadyLabel, setAlertWhenReadyLabel] = useState("Alert me when the report is ready to view");
  const updateSharedUsers = (sharedUsers: number[]) => {
    wizardContext.setSharedUsers(sharedUsers);
    setAlertWhenReadyLabel(
      (!sharedUsers || sharedUsers?.length === 0)
        ? "Alert me when the report is ready to view."
        : `Alert me and the other user${sharedUsers.length > 1 ? "s" : ""} when the report is ready to view.`);
  }

  // MMP HSD is also an acceptable format for Pattern of Care, but we currently remove that option for all volume provider reports
  const isPatternOfCareDisabled = wizardContext.providerType !== ProviderNetworkTypeEnum.Network || wizardContext.reportFilter !== "MA HSD Format" || wizardContext.servicingType === "Opportunities to Expand";
  const isRemoveNonHsdColumnsDisabled =
    !(wizardContext.reportFilter === "MA HSD Format" && !wizardContext.splitReport && selectedProjects.current.length > 1);

  const sharedUsersRequiredRoles = useMemo(() => {
    const newRequiredRoles: Roles[] = [];
    if (wizardContext.reportFilter === "CCIIO PPF Report")
      newRequiredRoles.push(Roles.CCIIO_PPF_REPORT_USER);

    if (wizardContext.reportFilter === "Distinct Servicing Providers")
      newRequiredRoles.push(Roles.DISTINCT_SERVICING_USER);

    if (wizardContext.providerType === ProviderNetworkTypeEnum.NetNew) 
      newRequiredRoles.push(Roles.NET_NEW_PROVIDER_USER);

    if (wizardContext.appendPatternOfCareProviders) 
      newRequiredRoles.push(Roles.PATTERN_OF_CARE_USER);

    return newRequiredRoles;
  }, [wizardContext.reportFilter])

  return (
    <PageSection>
      <QESCard title="Report" classNames="vr__reports-config-card mb-5">
        <div className="row">
          <div className="col-5 d-flex flex-column">
            <QESTextInput
              label="Report Name"
              required={true}
              isValid={wizardContext.reportName.length > 0}
              value={wizardContext.reportName}
              onChange={(e) => wizardContext.setReportName(e.target.value)}
              testId="volume-reports__report-name"
            />
            <div className="d-flex flex-row justify-content-between">
              <QESRadioGroup
                groupLabel="Report Type"
                groupContainerClass="mt-4"
                required={true}
              >
                {reportFormatRadioButtons}
              </QESRadioGroup>
              {(isProvidersRoute && wizardContext.reportType !== "zip-providers" && !["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) && (
                <QESRadioGroup
                  groupLabel="Filter"
                  groupContainerClass="mt-4"
                  required={true}
                >
                  {filterRadioButtons}
                </QESRadioGroup>
              )}
            </div>
          </div>
          <div className="col-5 offset-1">
            <SharedUsers
              exportType={getExportType()}
              customProjectIds={wizardContext.projects.filter(p => p.isChecked).map(p => p.customProjectId)}
              tooltipTitle="The user list has been filtered to only show users with access to the information contained in this volume report."
              networkAssessmentAnalysisType={wizardContext.servicingType == "Current Service Area" ? NetworkAssessmentAnalysisType.ServiceArea : NetworkAssessmentAnalysisType.Opportunity}
              networkProviderFilterType={wizardContext.networkProviderFilterType}
              updateSelectedSharedUsers={updateSharedUsers}
              requiredRoles={sharedUsersRequiredRoles}
            />
            <QESCheckbox
              label={alertWhenReadyLabel}
              onChange={(e) => wizardContext.setAlertWhenReady(e.target.checked)}
              testId="volume-reports__alert-when-ready"
              checked={wizardContext.alertWhenReady}
            />
            {wizardContext.reportType != "cciio" && <QESCheckbox
              label="Split into separate project reports"
              onChange={(e) => wizardContext.setSplitReport(e.target.checked)}
              testId="volume-reports__split-report"
              checked={wizardContext.splitReport}
            />}
            {(wizardContext.reportType === "cciio") && <QESCheckbox
              label="Include records that failed validation in CCIIO QHP Download"
              onChange={(e) => wizardContext.setIncludeInvalidCCIIORecords(e.target.checked)}
              testId="volume-reports__inlcude-invalid-cciio-records"
              checked={wizardContext.includeInvalidCCIIORecords}
            />}
            {isProvidersRoute ? (
              <QESCheckbox
                label="Include Cargo Column information"
                onChange={(e) => wizardContext.setIncludeCargoColumns(e.target.checked)}
                testId="volume-reports__include-cargo-columns"
                checked={wizardContext.includeCargoColumns}
              />
            ) : null}
            {!["cciio", "cciioDataValidations", "cciioDataValidationsSummary", "pci", "zip-class-aggregate"].includes(wizardContext.reportType) &&
              <QESCheckbox
                label="Remove Non-HSD columns"
                onChange={(e) => wizardContext.setRemoveNonHSDColumns(e.target.checked)}
                testId="remove-non-hsd-columns"
                disabled={isRemoveNonHsdColumnsDisabled}
                checked={wizardContext.removeNonHSDColumns}
              />}
            {wizardContext.reportType === "providers" && user.roles.includes(Roles.PATTERN_OF_CARE_USER) && wizardContext.projects.some((p) => p.isChecked && (p.exceptionCount > 0 || p.gapCount > 0) && p.showNextClosestProviders) ? (
              <TooltipWrapper text={isPatternOfCareDisabled ? "Select Network Providers in the Provider Filter and MA HSD Format to Append Pattern of Care Providers" : undefined} style={{ "marginTop": "-0.5rem" }}>
                <QESCheckbox
                  label="Append Pattern of Care Providers"
                  onChange={(e) => wizardContext.setAppendPatternOfCareProviders(e.target.checked)}
                  testId="volume-reports__append-pattern-of-care-providers"
                  checked={wizardContext.appendPatternOfCareProviders}
                  disabled={isPatternOfCareDisabled}
                />
              </TooltipWrapper>
            ) : null}
          </div>
        </div>
      </QESCard>

      <AccordionContainer>
        <Accordion
          title={`Projects: ${selectedProjects.current.length}`}
          testId="volume-reports-config__projects-accordion"
        >
          <ul>
            {selectedProjects.current.map((p) => <li key={p.customProjectId}>{p.projectName}</li>)}
          </ul>
        </Accordion>
      </AccordionContainer>

      {!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) && (
        <React.Fragment>

          {(wizardContext.reportType !== "specialtyaggregate") && (<AccordionContainer>
            <Accordion
              title={`Service Areas: ${noServiceAreaSelected ? "All" : `${selectedServiceAreas.current.length} States`} ${wizardContext.reportType !== "stateaggregate" ? ` and ${totalCounties.current} Counties` : ""}`}
              testId="volume-reports-config__service-areas-accordion"
            >
              {selectedServiceAreas.current.map((state) => {
                return (
                  <VolumeReportsStateAccordion key={state.displayName} state={state} />
                )
              })}
            </Accordion>
          </AccordionContainer>)}

          {(wizardContext.reportType !== "countyaggregate" && wizardContext.reportType !== "stateaggregate") && (<AccordionContainer>
            <Accordion
              title={`Specialties: ${noSpecialtySelected ? "All" : selectedSpecialties.current.length}`}
              testId="volume-reports-config__specialties-accordion"
            >
              <ul>
                {selectedSpecialties.current.map((specialty) => {
                  return <li key={specialty.displayName}>{specialty.displayName}</li>
                })}
              </ul>
            </Accordion>
          </AccordionContainer>)}
        </React.Fragment>
      )}
    </PageSection>
  );
};

export default VolumeReportsConfig;
